import assign from 'object-assign';
import {getLineTotals} from '@upi/data-layer-utils';
import {toSnakeCase} from '@util/StringUtils';

export const GACustomEvent = {
    FORM_SUBMIT: 'ajax_form_submit',
    IMPRESSION: 'view_item_list',
    ADD_TO_CART: 'add_to_cart',
    REMOVE_FROM_CART: 'remove_from_cart',
    PRODUCT_VIEW: 'view_item',
    PRODUCT_CLICK: 'select_item',
    PROMOTION_CLICK: 'select_promotion',
    PROMOTION_VIEW: 'view_promotion',
    VIEW_CART: 'view_cart',
    BEGIN_CHECKOUT: 'begin_checkout',
    ADD_SHIPPING_INFO: 'add_shipping_info',
    ADD_PAYMENT_INFO: 'add_payment_info',
    USER_LOGIN: 'login',
    REGISTRATION_PROGRESS: 'registration_progress',
    PURCHASE: 'purchase'
};

export const GACommands = {
    CONFIG: 'config',
    GET: 'get',
    SET: 'set',
    EVENT: 'event',
    CONSENT: 'consent'
};

const LIST_NAME_DEFAULT = 'General';

const googleAnalyticsProto = {
    sendEvent ( eventName, data ) {
        this.setDataLayer( {
            [GACommands.EVENT]: eventName,
            ...data
        } );

        console.log( '[dataLayer.GA4] send event', eventName, data );
        return this;
    },

    sendEcommerceEvent ( eventName, data ) {
        this.clearEcommerceData();
        this.setDataLayer( {
            [GACommands.EVENT]: eventName,
            ecommerce: {...data}
        } );

        console.log( '[dataLayer.GA4] send Ecommerce event', eventName, data );
        return this;
    },

    setDataLayer ( data ) {
        if ( !window.dataLayer ) {
            window.dataLayer = [];
        }

        window.dataLayer.push( data );

        return this;
    },

    createProductData ( item ) {
        const fieldName = item.actionField || LIST_NAME_DEFAULT;
        return {
            item_id: item.sku,
            item_name: item.kitName,
            item_brand: item.brandName || item.productName,
            item_category: item.productFamily,
            item_list_name: fieldName,
            item_list_id: toSnakeCase( fieldName ),
            price: item.customerPrice || item.catalogPrice,
            quantity: item.quantity || 1,
            index: item.position || 1
        };
    },

    createPromotionData ( {
                              index = 1,
                              promoId = '',
                              promoName = '',
                              promoCreativeName = '',
                              promoPosition = ''
                          } ) {
        return {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: promoCreativeName,
            creative_slot: promoPosition,
            index
        }
    },

    clearEcommerceData () {
        console.log( '[dataLayer.GA4] clear Ecommerce Data' );
        this.setDataLayer( {ecommerce: null} );
    }
};

export const googleAnalyticsLayer = assign( {}, googleAnalyticsProto, {
    crossSellItems ( items = [] ) {
        if ( items.length > 0 && items[0] ) {
            const fieldName = items[0].actionField || LIST_NAME_DEFAULT;
            this.sendEcommerceEvent( GACustomEvent.IMPRESSION, {
                item_list_id: toSnakeCase( fieldName ),
                item_list_name: fieldName,
                items: items.map( this.createProductData )
            } );
        }
        else {
            return;
        }
    },

    itemsAdded ( items = [] ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }

        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.ADD_TO_CART, {
            currency: 'USD',
            value: getLineTotals( products ),
            items: products
        } );
    },

    itemsRemoved ( items = [] ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }
        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.REMOVE_FROM_CART, {
            currency: 'USD',
            value: getLineTotals( products ),
            items: products
        } );
    },

    productView ( item ) {
        const itemData = this.createProductData( item );

        this.sendEcommerceEvent( GACustomEvent.PRODUCT_VIEW, {
            currency: 'USD',
            value: itemData.price,
            items: [itemData]
        } );
    },

    productClick ( item ) {
        const itemData = this.createProductData( item );
        const fieldName = item.actionField || LIST_NAME_DEFAULT;

        this.sendEcommerceEvent( GACustomEvent.PRODUCT_CLICK, {
            item_list_id: toSnakeCase( fieldName ),
            item_list_name: fieldName,
            items: [itemData]
        } );
    },

    promoView ( promo ) {
        const promotion = this.createPromotionData( promo );

        this.sendEcommerceEvent( GACustomEvent.PROMOTION_VIEW, {
            ...promotion,
            items: [{
                item_id: promo.promoId,
                item_name: promo.promoName
            }]
        } );
    },

    promoClick ( promo ) {
        const promotion = this.createPromotionData( promo );

        this.sendEcommerceEvent( GACustomEvent.PROMOTION_CLICK, {
            ...promotion,
            items: [{
                item_id: promo.promoId,
                item_name: promo.promoName
            }]
        } );
    },

    viewCart ( items ) {
        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.VIEW_CART, {
            currency: 'USD',
            value: getLineTotals( products ),
            items: items.map( this.createProductData )
        } );
    },

    beginCheckout ( items ) {
        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.BEGIN_CHECKOUT, {
            currency: 'USD',
            value: getLineTotals( products ),
            items: items.map( this.createProductData )
        } );
    },

    purchaseComplete ( {items, orderNumber, promoCode, subtotal, shippingTotal, tax} ) {
        this.sendEcommerceEvent( GACustomEvent.PURCHASE, {
            currency: 'USD',
            transaction_id: orderNumber,
            value: subtotal,
            coupon: promoCode,
            shipping: shippingTotal,
            tax,
            items: items.map( this.createProductData )
        } );
    },

    addPaymentInfo ( {paymentMethodType, items} ) {
        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.ADD_PAYMENT_INFO, {
            payment_type: paymentMethodType,
            currency: 'USD',
            value: getLineTotals( products ),
            items: items.map( this.createProductData )
        } );
    },
    addShippingInfo ( {shippingMethod, items} ) {
        const products = items.map( this.createProductData );

        this.sendEcommerceEvent( GACustomEvent.ADD_SHIPPING_INFO, {
            shipping_tier: shippingMethod,
            currency: 'USD',
            value: getLineTotals( products ),
            items: items.map( this.createProductData )
        } );
    },

    formSubmit ( {formName} ) {
        this.sendEvent( GACustomEvent.FORM_SUBMIT, {
            form_name: formName
        } );
    },

    /**
     *
     * @param userId
     * @param signInMethod
     *
     * Common sign in methods would include:
     * "Google": Indicates login through Google OAuth or Google Sign-In.
     * "Facebook": Indicates login through Facebook's authentication system.
     * "Twitter": Indicates login through Twitter authentication.
     * "Email": Indicates a standard email and password login method.
     * "Apple": Indicates login through Apple's Sign in with Apple feature.
     * "Microsoft": Indicates login through Microsoft's account system.
     * "LinkedIn": Indicates login through LinkedIn's authentication system.
     * "GitHub": Indicates login through GitHub's OAuth authentication.
     * "Phone": Indicates login through a phone number and OTP (One Time Password).
     * "Anonymous": Indicates an anonymous login method, where users do not provide identifiable information.
     * "Custom": Indicates a custom login method specific to the application, which does not fall under the common categories.
     */
    userLogin ( {signInMethod} ) {
        this.sendEvent( GACustomEvent.USER_LOGIN, {
            method: signInMethod
        } );
    },

    registrationProgress ( {step, viewCount, result} ) {
        this.sendEvent( GACustomEvent.REGISTRATION_PROGRESS, {
            step: step,
            view_count: viewCount,
            result: result
        } );
    }
} );
